import { useT } from "@/i18n";
import { settingsShortcutsMessages } from "@/i18n/messages/settings-shortcuts";
import { cn } from "@/lib/cn";
import {
  shortcutKeyboardLabel,
  shortcutPlatform,
  type ShortcutBinding,
  type ShortcutPlatform,
} from "@/lib/shortcut-registry";

type Key = { code: string; width: number; x: number; y: number };
const UNIT = 43;
// A key wider than this (the space bar) would shrink every other key in the
// crop, so only the end nearest the other pressed keys is framed.
const WIDE_KEY = 3 * UNIT;
const WIDE_KEY_FRAMED = 2 * UNIT;
const MODIFIERS = new Set(["Control", "Alt", "Meta", "Shift"]);
const canonicalCode = (code: string) =>
  code.replace(/^(Control|Alt|Meta|Shift)(?:Left|Right)$/, "$1");

function keyboardKeys(macos: boolean): Key[] {
  const unit = UNIT;
  const rows: [string, number][][] = [
    [
      ["Escape", 1.1],
      ...Array.from({ length: 12 }, (_, i): [string, number] => [
        `F${i + 1}`,
        1,
      ]),
      ["Delete", 1.7],
    ],
    [
      ["Backquote", 1],
      ..."1234567890".split("").map((n): [string, number] => [`Digit${n}`, 1]),
      ["Minus", 1],
      ["Equal", 1],
      ["Backspace", 1.8],
    ],
    [
      ["Tab", 1.5],
      ..."QWERTYUIOP".split("").map((n): [string, number] => [`Key${n}`, 1]),
      ["BracketLeft", 1],
      ["BracketRight", 1],
      ["Backslash", 1],
    ],
    [
      ["CapsLock", 1.8],
      ..."ASDFGHJKL".split("").map((n): [string, number] => [`Key${n}`, 1]),
      ["Semicolon", 1],
      ["Quote", 1],
      ["Enter", 2],
    ],
    [
      ["ShiftLeft", 2.25],
      ..."ZXCVBNM".split("").map((n): [string, number] => [`Key${n}`, 1]),
      ["Comma", 1],
      ["Period", 1],
      ["Slash", 1],
      ["ShiftRight", 2.55],
    ],
    macos
      ? [
          ["Fn", 1],
          ["ControlLeft", 1],
          ["AltLeft", 1],
          ["MetaLeft", 1.3],
          ["Space", 5.2],
          ["MetaRight", 1.3],
          ["AltRight", 1],
          ["ArrowLeft", 1],
          ["ArrowUp", 1],
          ["ArrowRight", 1],
        ]
      : [
          ["ControlLeft", 1.2],
          ["MetaLeft", 1],
          ["AltLeft", 1.2],
          ["Space", 5.5],
          ["AltRight", 1.2],
          ["ContextMenu", 1],
          ["ControlRight", 1.2],
          ["ArrowLeft", 1],
          ["ArrowUp", 1],
          ["ArrowRight", 1],
        ],
  ];
  return rows.flatMap((row, index) => {
    let x = 8;
    return row.map(([code, width]) => {
      const key = { code, width: width * unit, x, y: 8 + index * 41 };
      x += key.width + 4;
      return key;
    });
  });
}

function keyLegend(
  code: string,
  platform: ReturnType<typeof shortcutPlatform>,
) {
  const base = canonicalCode(code);
  const punctuation: Record<string, string> = {
    Backquote: "`",
    Minus: "−",
    Equal: "=",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Semicolon: ";",
    Quote: "'",
    Comma: ",",
    Period: ".",
    Slash: "/",
    ArrowLeft: "←",
    ArrowRight: "→",
    ArrowUp: "↑↓",
    Fn: "fn",
    ContextMenu: "☰",
    Escape: "Esc",
    // A real space bar is blank.
    Space: "",
  };
  const text = punctuation[base] ?? shortcutKeyboardLabel(base, platform);
  const symbol =
    platform === "macos"
      ? (
          { Control: "⌃", Alt: "⌥", Meta: "⌘", Shift: "⇧" } as Record<
            string,
            string
          >
        )[base]
      : undefined;
  return {
    text:
      platform === "macos" && MODIFIERS.has(base) ? text.toLowerCase() : text,
    symbol,
    // Windows keyboards print the logo alone on this key.
    windowsLogo: platform === "windows" && base === "Meta",
  };
}

/** The four-pane Windows logo, centred on a key. */
function WindowsLogo({ x, y }: { x: number; y: number }) {
  return (
    <path
      transform={`translate(${x} ${y})`}
      d="M-6.75-6.75h6v6h-6zM.75-6.75h6v6h-6zM-6.75.75h6v6h-6zM.75.75h6v6h-6z"
      fill="var(--color-primary)"
    />
  );
}

/** A pressed key's cap sinks onto its base: quick down, eased release. */
const capMotion = (pressed: boolean) => ({
  transform: pressed ? "translateY(2px)" : undefined,
  transition: `transform ${pressed ? 70 : 160}ms cubic-bezier(0.16, 1, 0.3, 1)`,
});

export function ShortcutKeyboardCrop({
  binding,
  large = false,
  pressed = false,
  platform = shortcutPlatform(),
}: {
  binding: ShortcutBinding;
  large?: boolean;
  /** The shortcut's keys are held down right now (an animated demo). */
  pressed?: boolean;
  platform?: ShortcutPlatform;
}) {
  const t = useT(settingsShortcutsMessages);
  const active = [...(binding.holdKeys ?? []), ...binding.keys];
  const keys = keyboardKeys(platform === "macos");
  const lit = (key: Key) =>
    active.includes(key.code) ||
    (key.code === "ArrowUp" && active.includes("ArrowDown")) ||
    (!key.code.endsWith("Right") && active.includes(canonicalCode(key.code)));
  const selected = keys.filter(lit);
  if (!selected.length)
    return (
      <span className="text-appkit-small text-muted">{t("noShortcut")}</span>
    );
  const framed = (key: Key) => {
    const others = selected.filter((other) => other !== key);
    if (!others.length || key.width <= WIDE_KEY)
      return { x: key.x, width: key.width };
    const toward =
      others.reduce((sum, other) => sum + other.x + other.width / 2, 0) /
      others.length;
    return {
      x:
        toward < key.x + key.width / 2
          ? key.x
          : key.x + key.width - WIDE_KEY_FRAMED,
      width: WIDE_KEY_FRAMED,
    };
  };
  const left = Math.min(...selected.map((k) => framed(k).x)) - 7;
  const top = Math.min(...selected.map((k) => k.y)) - 7;
  const right =
    Math.max(...selected.map((k) => framed(k).x + framed(k).width)) + 7;
  const bottom = Math.max(...selected.map((k) => k.y + 37)) + 9;
  const multi = bottom - top > 60;
  const ratio = multi ? 1.75 : 3.15;
  const width = Math.max(right - left, (bottom - top) * ratio);
  const height = width / ratio;
  return (
    <svg
      viewBox={`${Math.max(0, (left + right - width) / 2)} ${Math.max(0, (top + bottom - height) / 2)} ${width} ${height}`}
      role="img"
      aria-label={t("keyboard", {
        platform: platform === "macos" ? "Mac" : "Windows",
        keys: active
          .map((key) => shortcutKeyboardLabel(key, platform))
          .join(", "),
      })}
      data-layout={multi ? "multi" : "single"}
      className={cn(
        "shrink-0 rounded-control-sm bg-surface-selected",
        large
          ? multi
            ? "h-[88px] w-[154px]"
            : "h-[49px] w-[154px]"
          : multi
            ? "h-[60px] w-[104px]"
            : "h-[33px] w-[104px]",
      )}
    >
      {keys.map((key) => {
        const highlighted = lit(key);
        const { text, symbol, windowsLogo } = keyLegend(key.code, platform);
        return (
          <g
            key={key.code}
            className={highlighted ? "text-accent" : "text-muted"}
          >
            <rect
              x={key.x}
              y={key.y + 2}
              width={key.width}
              height={37}
              rx={5}
              fill="var(--color-surface)"
            />
            <g style={highlighted ? capMotion(pressed) : undefined}>
              <rect
                x={key.x}
                y={key.y}
                width={key.width}
                height={37}
                rx={5}
                fill={
                  highlighted
                    ? "var(--color-accent)"
                    : "var(--color-surface-selected)"
                }
                fillOpacity={highlighted ? (pressed ? 0.4 : 0.2) : 1}
                stroke="currentColor"
                strokeWidth={0.8}
              />
              {windowsLogo ? (
                <WindowsLogo x={key.x + key.width / 2} y={key.y + 18.5} />
              ) : symbol ? (
                <>
                  <text
                    x={key.x + 5}
                    y={key.y + 30}
                    fontSize={7.5}
                    fill="var(--color-primary)"
                  >
                    {text}
                  </text>
                  <text
                    x={key.x + key.width - 6}
                    y={key.y + 13}
                    textAnchor="end"
                    fontSize={11}
                    fill="currentColor"
                  >
                    {symbol}
                  </text>
                </>
              ) : (
                <text
                  x={key.x + key.width / 2}
                  y={key.y + 24}
                  textAnchor="middle"
                  fontSize={text.length > 3 ? 8 : 12}
                  fill="var(--color-primary)"
                >
                  {text}
                </text>
              )}
            </g>
          </g>
        );
      })}
    </svg>
  );
}
